import { CheckCircle, XCircle, AlertCircle, Info, Loader2, X } from 'lucide-react';
import type { Toast } from './types';

interface ToastNotificationsProps {
  toasts: Toast[];
  onRemove?: (id: string) => void;
}

const toastStyles = {
  success: 'bg-green-50/95 dark:bg-green-900/30 border-2 border-green-300 dark:border-green-700 text-green-800 dark:text-green-200',
  error: 'bg-red-50/95 dark:bg-red-900/30 border-2 border-red-300 dark:border-red-700 text-red-800 dark:text-red-200',
  warning: 'bg-yellow-50/95 dark:bg-yellow-900/30 border-2 border-yellow-300 dark:border-yellow-700 text-yellow-800 dark:text-yellow-200',
  info: 'bg-blue-50/95 dark:bg-blue-900/30 border-2 border-blue-300 dark:border-blue-700 text-blue-800 dark:text-blue-200',
  loading: 'bg-gray-50/95 dark:bg-gray-800/30 border-2 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200'
} as const;

const ToastIcon = ({ type }: { type: Toast['type'] }) => {
  const iconProps = { className: 'w-5 h-5' };

  switch (type) {
    case 'success':
      return <CheckCircle {...iconProps} className="w-5 h-5 text-green-600 dark:text-green-400" />;
    case 'error':
      return <XCircle {...iconProps} className="w-5 h-5 text-red-600 dark:text-red-400" />;
    case 'warning':
      return <AlertCircle {...iconProps} className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />;
    case 'info':
      return <Info {...iconProps} className="w-5 h-5 text-blue-600 dark:text-blue-400" />;
    case 'loading':
      return <Loader2 {...iconProps} className="w-5 h-5 text-gray-600 dark:text-gray-400 animate-spin" />;
    default:
      return null;
  }
};

export function ToastNotifications({ toasts, onRemove }: ToastNotificationsProps) {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[1500] space-y-2 max-w-md">
      {toasts.map(toast => (
        <div
          key={toast.id}
          className={`flex items-start gap-3 min-w-[320px] px-4 py-3 rounded-lg shadow-xl transform transition-all duration-300 animate-slide-in backdrop-blur-sm ${toastStyles[toast.type]}`}
          role="alert"
          aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
        >
          <div className="flex-shrink-0 mt-0.5">
            <ToastIcon type={toast.type} />
          </div>
          <p className="flex-1 text-sm font-medium leading-relaxed">{toast.message}</p>
          {onRemove && toast.type !== 'loading' && (
            <button type="button"
              onClick={() => onRemove(toast.id)}
              className="flex-shrink-0 inline-flex rounded-md p-1 hover:bg-black/5 dark:hover:bg-white/5 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1"
              aria-label="Cerrar notificación"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}



